// =============================================
// API Service
// =============================================

import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/authStore';

// Export API base URL for components that use fetch directly
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api';

// Helper function to get full API URL
export const getApiUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_BASE_URL}/${cleanPath}`;
};

// Types — imported from @kipikid/shared, re-exported for backward compatibility
import type {
  ApiResponse,
  Child,
  AttendanceRecord,
  MarkAttendanceRequest,
  SyncType,
  SyncResponse,
  ChildSyncDto,
  ChildUpdateDto,
  KindergartenDaySettings,
} from '@kipikid/shared';

export type {
  ApiResponse,
  Child,
  AttendanceRecord,
  MarkAttendanceRequest,
  SyncType,
  SyncResponse,
  ChildSyncDto,
  ChildUpdateDto,
  KindergartenDaySettings,
};

// ==========================================
// Token Refresh - ניהול חידוש טוקן אוטומטי
// ==========================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// חידוש טוקן באמצעות refresh token
// משתמש ב-axios ישיר (לא ב-instance) כדי למנוע לולאה אינסופית של 401
async function attemptTokenRefresh(): Promise<string | null> {
  const authState = useAuthStore.getState();
  const { refreshToken } = authState;

  if (!refreshToken) {
    if (import.meta.env.DEV) console.warn('[API] No refresh token available, cannot refresh');
    return null;
  }

  try {
    // ייבוא דינמי של deviceFingerprint
    const { getDeviceFingerprint } = await import('../utils/deviceFingerprint');
    const deviceFingerprint = await getDeviceFingerprint();

    const functionKey = import.meta.env.VITE_FUNCTION_KEY;
    const baseUrl = import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api';

    // קריאה ישירה ל-API בלי ה-interceptor (AuthorizationLevel.Anonymous)
    const response = await axios.post(
      `${baseUrl}/auth/refresh`,
      {
        refreshToken,
        deviceFingerprint,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(functionKey && { 'x-functions-key': functionKey }),
        },
        timeout: 15000,
      }
    );

    const result = response.data;

    if (result.success && result.data) {
      const newAccessToken = result.data.accessToken || result.data.AccessToken;
      const newRefreshToken = result.data.refreshToken || result.data.RefreshToken;
      const newExpiresAt = result.data.expiresAt || result.data.ExpiresAt;

      if (newAccessToken) {
        useAuthStore.getState().setToken(newAccessToken);
      }
      if (newRefreshToken) {
        useAuthStore.getState().setRefreshToken(newRefreshToken);
      }
      if (newExpiresAt) {
        useAuthStore.getState().setTokenExpiresAt(newExpiresAt);
      }

      if (import.meta.env.DEV) console.log('[API] Token refreshed successfully');
      return newAccessToken;
    } else {
      console.error('[API] Token refresh failed:', result.message);
      return null;
    }
  } catch (error) {
    console.error('[API] Token refresh request failed:', error);
    return null;
  }
}

// פונקציה ציבורית לחידוש טוקן פרואקטיבי (לפני שפג)
export async function proactiveTokenRefresh(): Promise<boolean> {
  const authState = useAuthStore.getState();

  if (!authState.isTokenExpiringSoon()) {
    return true; // הטוקן עדיין תקף
  }

  if (!authState.refreshToken) {
    return false; // אין refresh token
  }

  if (import.meta.env.DEV) console.log('[API] Proactive token refresh - token expiring soon...');
  const newToken = await attemptTokenRefresh();
  return newToken !== null;
}

// API Instance
const createApiInstance = (): AxiosInstance => {
  const functionKey = import.meta.env.VITE_FUNCTION_KEY;

  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      ...(functionKey && { 'x-functions-key': functionKey }),
    },
  });

  // Request interceptor - add auth token
  instance.interceptors.request.use(
    (config) => {
      // Primary source: Zustand store (backed by sessionStorage)
      let token = useAuthStore.getState().token;

      // Fallback: read directly from sessionStorage in case store is not yet hydrated
      if (!token) {
        try {
          const stored = sessionStorage.getItem('kipikid-auth');
          if (stored) {
            const parsed = JSON.parse(stored);
            token = parsed.state?.token;
          }
        } catch (e) {
          if (import.meta.env.DEV) console.error('[API] Error reading auth from sessionStorage:', e);
        }
      }

      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        if (import.meta.env.DEV) console.warn('[API] No token found — request will be unauthenticated');
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - טיפול ב-401 עם חידוש טוקן אוטומטי
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const originalRequest = error.config as any;

      if (error.response?.status === 401) {
        // Auth endpoints מטפלים ב-401 בעצמם
        const requestUrl = error.config?.url || '';
        const isAuthRequest = requestUrl.includes('/auth/');

        if (isAuthRequest) {
          if (import.meta.env.DEV) console.log('[API] 401 on auth endpoint, letting caller handle it');
          return Promise.reject(error);
        }

        const user = useAuthStore.getState().user;
        const hasRefreshToken = !!useAuthStore.getState().refreshToken;

        // ניסיון חידוש טוקן אוטומטי — לכל סוגי המשתמשים
        // (טאבלט, הורה, מנהל, מנהל מערכת)
        if (hasRefreshToken) {
          // כבר ניסינו retry - אימות נכשל לצמיתות
          if (originalRequest._retry) {
            console.error('[API] Token refresh already attempted, auth failed permanently');
            if (user?.role === 'tablet') {
              useAuthStore.getState().clearAuth();
            } else {
              useAuthStore.getState().logout();
            }
            return Promise.reject(error);
          }

          // חידוש כבר מתבצע - נוסיף לתור
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return instance(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await attemptTokenRefresh();
            if (newToken) {
              processQueue(null, newToken);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return instance(originalRequest);
            } else {
              // חידוש נכשל - ניקוי אימות
              processQueue(new Error('Token refresh failed'));
              if (user?.role === 'tablet') {
                useAuthStore.getState().clearAuth();
              } else {
                useAuthStore.getState().logout();
              }
              return Promise.reject(error);
            }
          } catch (refreshError) {
            processQueue(refreshError);
            if (user?.role === 'tablet') {
              useAuthStore.getState().clearAuth();
            } else {
              useAuthStore.getState().logout();
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          // אין refresh token — logout
          if (import.meta.env.DEV) console.log('[API] 401 received, no refresh token available, logging out');
          if (user?.role === 'tablet') {
            useAuthStore.getState().clearAuth();
          } else {
            useAuthStore.getState().logout();
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const api = createApiInstance();

// ==========================================
// Attendance API
// ==========================================

export const attendanceApi = {
  // Mark child as arrived
  markArrival: async (request: MarkAttendanceRequest): Promise<ApiResponse<AttendanceRecord>> => {
    const response = await api.post<ApiResponse<AttendanceRecord>>('/attendance/mark', request);
    return response.data;
  },
  
  // Cancel arrival
  cancelArrival: async (childPublicId: string, kindergartenPublicId: string, reason?: string): Promise<ApiResponse<void>> => {
    const response = await api.post<ApiResponse<void>>('/attendance/cancel', {
      childPublicId,
      kindergartenPublicId,
      reason,
    });
    return response.data;
  },
  
  // Get child status
  getChildStatus: async (childPublicId: string): Promise<ApiResponse<AttendanceRecord | null>> => {
    const response = await api.get<ApiResponse<AttendanceRecord | null>>(`/attendance/status/${childPublicId}`);
    return response.data;
  },
  
  // Get batch status for multiple children (parent batch endpoint)
  getChildrenStatusBatch: async (childPublicIds: string[]): Promise<ApiResponse<AttendanceRecord[]>> => {
    const response = await api.post<ApiResponse<AttendanceRecord[]>>('/attendance/status-batch', {
      childPublicIds,
    });
    return response.data;
  },

  // Get kindergarten attendance
  getKindergartenAttendance: async (kindergartenPublicId: string, date?: string): Promise<ApiResponse<AttendanceRecord[]>> => {
    const params = date ? { date } : {};
    const response = await api.get<ApiResponse<AttendanceRecord[]>>(`/attendance/kindergarten/${kindergartenPublicId}`, { params });
    return response.data;
  },
};

// ==========================================
// Sync API
// ==========================================

export const syncApi = {
  // Full sync
  fullSync: async (kindergartenPublicId: string): Promise<ApiResponse<SyncResponse>> => {
    const response = await api.post<ApiResponse<SyncResponse>>('/sync/full', {
      kindergartenPublicId,
      syncType: 'Full',
    });
    return response.data;
  },
  
  // Delta sync
  deltaSync: async (kindergartenPublicId: string, lastSyncAt: string): Promise<ApiResponse<SyncResponse>> => {
    const response = await api.post<ApiResponse<SyncResponse>>('/sync/delta', {
      kindergartenPublicId,
      lastSyncAt,
      syncType: 'Delta',
    });
    return response.data;
  },
  
  // Check for updates
  checkForUpdates: async (kindergartenPublicId: string, lastSync: string): Promise<{ hasUpdates: boolean }> => {
    const response = await api.get<ApiResponse<{ hasUpdates: boolean }>>(`/sync/check/${kindergartenPublicId}`, {
      params: { lastSync },
    });
    return response.data.data || { hasUpdates: false };
  },
  
  // Health check
  healthCheck: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await api.get<{ status: string; timestamp: string }>('/sync/health');
    return response.data;
  },
};

// ==========================================
// Daily API
// ==========================================

export const dailyApi = {
  // Get expected children
  getExpectedChildren: async (kindergartenId: number, date?: string): Promise<ApiResponse<Child[]>> => {
    const params = date ? { date } : {};
    const response = await api.get<ApiResponse<Child[]>>(`/daily/expected/${kindergartenId}`, { params });
    return response.data;
  },

  // Update child status (sick/vacation)
  updateChildStatus: async (childId: number, reason: 'sick' | 'vacation' | 'other', date?: string): Promise<ApiResponse<void>> => {
    const response = await api.post<ApiResponse<void>>('/daily/child-status', {
      childId,
      reason,
      date,
    });
    return response.data;
  },
};

// ==========================================
// Parent Calendar Events (System + KG overrides)
// ==========================================

export interface ParentCalendarEventDto {
  date: string;          // "yyyy-MM-dd"
  eventType: number;     // 1=חג, 2=חול המועד, 3=חופשה, 5=אחר
  eventName: string;
  operationMode: number; // 0=סגור, 1=רגיל, 2=חלקי
  startTime?: string;
  endTime?: string;
  notes?: string;
  isKindergartenOverride: boolean;
  kindergartenName?: string;
}

// ==========================================
// Child Calendar API (Absences)
// ==========================================

export interface ChildAbsence {
  // Note: Using publicId instead of internal Id for security
  publicId: string;
  childPublicId: string;
  childName: string;
  startDate: string; // DateOnly format: "YYYY-MM-DD"
  endDate: string;
  reason: number; // 1=vacation, 2=sick, 3=other
  notes?: string;
  createdAt: string;
}

export interface CreateAbsenceRequest {
  childPublicId: string;
  startDate: string;
  endDate: string;
  reason: number;
  notes?: string;
}

export interface UpdateAbsenceRequest {
  startDate?: string;
  endDate?: string;
  reason?: number;
  notes?: string;
}

export interface DeleteAbsenceDaysRequest {
  singleDate?: string;
  rangeStart?: string;
  rangeEnd?: string;
  deleteAll?: boolean;
}

export const childCalendarApi = {
  // Get all absences for user's children
  getAbsences: async (fromDate?: string, toDate?: string): Promise<ApiResponse<ChildAbsence[]>> => {
    const params: Record<string, string> = {};
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    const response = await api.get<ApiResponse<ChildAbsence[]>>('/children/absences', { params });
    return response.data;
  },

  // Create a new absence
  createAbsence: async (request: CreateAbsenceRequest): Promise<ApiResponse<ChildAbsence>> => {
    const response = await api.post<ApiResponse<ChildAbsence>>('/children/absences', request);
    return response.data;
  },

  // Update an existing absence (using publicId)
  updateAbsence: async (absencePublicId: string, request: UpdateAbsenceRequest): Promise<ApiResponse<ChildAbsence>> => {
    const response = await api.put<ApiResponse<ChildAbsence>>(`/children/absences/${absencePublicId}`, request);
    return response.data;
  },

  // Delete an absence (fully or partially) (using publicId)
  deleteAbsence: async (absencePublicId: string, deleteRequest?: DeleteAbsenceDaysRequest): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/children/absences/${absencePublicId}`, {
      data: deleteRequest,
    });
    return response.data;
  },
};

// ==========================================
// Auth API
// ==========================================

// ==========================================
// Parent API
// ==========================================

export interface ChildRelation {
  childPublicId: string;
  childFirstName: string;
  childLastName: string;
  childPhotoUrl?: string;
  // Note: Using kindergartenPublicId instead of internal Id for security
  kindergartenPublicId: string;
  kindergartenName: string;
  relationType: string;
  isPayingParent: boolean;
  alertOrder?: number;
  receiveAlerts: boolean;
  canMarkAttendance: boolean;
  // Arrival times
  sundayArrivalTime?: string;
  mondayArrivalTime?: string;
  tuesdayArrivalTime?: string;
  wednesdayArrivalTime?: string;
  thursdayArrivalTime?: string;
  fridayArrivalTime?: string;
  // Status
  isOnVacationToday?: boolean;
  vacationReason?: number; // 1=חופשה, 2=מחלה, 3=אחר
}

export const parentApi = {
  // Get my children
  getMyChildren: async (): Promise<ApiResponse<ChildRelation[]>> => {
    const response = await api.get<ApiResponse<ChildRelation[]>>('/children');
    return response.data;
  },

  // Get calendar events (system + KG overrides)
  getCalendarEvents: async (year: number): Promise<ApiResponse<ParentCalendarEventDto[]>> => {
    const response = await api.get<ApiResponse<ParentCalendarEventDto[]>>('/parent/calendar-events', { params: { year } });
    return response.data;
  },
};

// ==========================================
// Notifications API - הודעות להורים
// ==========================================

export interface UserNotification {
  publicId: string;
  type: string; // 'KindergartenMessage', 'Alert', 'System', etc.
  title: string;
  body: string;
  createdAt: string;
  isRead: boolean;
  readAt?: string;
  actionUrl?: string;
  metadata?: {
    messageId?: string;
    childPublicId?: string;
    childName?: string;
    kindergartenName?: string;
    isGeneral?: boolean;
    [key: string]: unknown;
  };
}

export const notificationsApi = {
  // קבלת הודעות המשתמש
  getNotifications: async (page = 1, pageSize = 50): Promise<ApiResponse<UserNotification[]>> => {
    const response = await api.get<ApiResponse<UserNotification[]>>('/notifications', {
      params: { page, pageSize },
    });
    return response.data;
  },

  // סימון הודעה כנקראה
  markAsRead: async (notificationPublicId: string): Promise<ApiResponse<boolean>> => {
    const response = await api.post<ApiResponse<boolean>>(`/notifications/${notificationPublicId}/read`);
    return response.data;
  },
};

// Child detail interface
export interface ChildDetail {
  publicId: string;
  firstName: string;
  lastName: string;
  birthDate?: string;
  gender?: number; // 1=זכר, 2=נקבה
  photoUrl?: string;
  // Note: Using kindergartenPublicId instead of internal Id for security
  kindergartenPublicId: string;
  kindergartenName: string;
  notes?: string;
  sundayArrivalTime?: string;
  mondayArrivalTime?: string;
  tuesdayArrivalTime?: string;
  wednesdayArrivalTime?: string;
  thursdayArrivalTime?: string;
  fridayArrivalTime?: string;
  createdAt: string;
  updatedAt?: string;
}

// Create child request
export interface CreateChildRequest {
  firstName: string;
  lastName: string;
  birthDate?: string;
  gender?: number;
  // Note: Using kindergartenPublicId instead of internal Id for security
  kindergartenPublicId: string;
  photoBase64?: string;
  photoContentType?: string;
  notes?: string;
  sundayArrivalTime?: string;
  mondayArrivalTime?: string;
  tuesdayArrivalTime?: string;
  wednesdayArrivalTime?: string;
  thursdayArrivalTime?: string;
  fridayArrivalTime?: string;
}

// Update child request
export interface UpdateChildRequest {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  gender?: number;
  // Note: Using kindergartenPublicId instead of internal Id for security
  kindergartenPublicId?: string;
  photoBase64?: string;
  photoContentType?: string;
  removePhoto?: boolean;
  notes?: string;
  sundayArrivalTime?: string;
  mondayArrivalTime?: string;
  tuesdayArrivalTime?: string;
  wednesdayArrivalTime?: string;
  thursdayArrivalTime?: string;
  fridayArrivalTime?: string;
}

export const childApi = {
  // Create a new child
  createChild: async (request: CreateChildRequest): Promise<ApiResponse<ChildDetail>> => {
    const response = await api.post<ApiResponse<ChildDetail>>('/children', request);
    return response.data;
  },

  // Update child
  updateChild: async (childPublicId: string, request: UpdateChildRequest): Promise<ApiResponse<ChildDetail>> => {
    const response = await api.put<ApiResponse<ChildDetail>>(`/children/${childPublicId}`, request);
    return response.data;
  },

  // Get child detail
  getChildDetail: async (childPublicId: string): Promise<ApiResponse<ChildDetail>> => {
    const response = await api.get<ApiResponse<ChildDetail>>(`/children/${childPublicId}/detail`);
    return response.data;
  },

  // Delete child
  deleteChild: async (childPublicId: string): Promise<ApiResponse<boolean>> => {
    const response = await api.delete<ApiResponse<boolean>>(`/children/${childPublicId}`);
    return response.data;
  },
};

// Kindergarten interface for API
export interface KindergartenOperatingDayFromApi {
  dayOfWeek: number;       // 1=Sunday..6=Friday
  latestArrivalTime: string; // "HH:mm"
}

export interface KindergartenFromApi {
  // Note: Using publicId instead of internal Id for security
  publicId: string;
  name: string;
  city: string;
  street: string;
  houseNumber: string;
  operatingDays?: KindergartenOperatingDayFromApi[];
}

export const kindergartenApi = {
  // Get all kindergartens
  getAll: async (): Promise<ApiResponse<KindergartenFromApi[]>> => {
    const response = await api.get<ApiResponse<KindergartenFromApi[]>>('/kindergartens');
    return response.data;
  },
};

// ==========================================
// Admin API - ממשק מנהל מערכת
// ==========================================

export interface OrganizationDto {
  publicId: string;
  name: string;
  organizationType: number;
  organizationTypeName: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  kindergartenCount: number;
  isActive: boolean;
  createdAt: string;
}

export interface CreateOrganizationRequest {
  name: string;
  organizationType: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  isActive?: boolean;
}

export interface CreateOrganizationManagerRequest {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  username: string;
  password: string;
}

export interface AdminStaffDto {
  publicId: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  photoUrl?: string;
  roleTypeId: number;
  roleName: string;
  dayAssignments: StaffDayAssignmentDto[];
  isActive: boolean;
  createdAt: string;
}

export interface StaffDayAssignmentDto {
  dayOfWeek: number;
  kindergartenPublicId: string;
  kindergartenName: string;
}

export interface CreateStaffRequest {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  username?: string;
  password?: string;
  roleTypeId: number;
  dayAssignments: { dayOfWeek: number; kindergartenPublicId: string }[];
  photoBase64?: string;
  organizationPublicId?: string;
}

export interface UpdateStaffRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  roleTypeId?: number;
  dayAssignments?: { dayOfWeek: number; kindergartenPublicId: string }[];
  photoBase64?: string;
}

export interface AdminKindergartenSearchDto {
  publicId: string;
  name: string;
  city: string;
  street: string;
  houseNumber?: string;
  code: string;
}

export interface RoleTypeDto {
  id: number;
  name: string;
  nameHebrew: string;
  category: number;
}

export interface AdminDashboardStats {
  totalOrganizations: number;
  totalKindergartens: number;
  totalStaff: number;
  totalChildren: number;
  activeOrganizations: number;
}

export interface AdminKindergartenDto {
  publicId: string;
  name: string;
  code: string;
  city: string;
  street: string;
  houseNumber?: string;
  phone?: string;
  phone2?: string;
  maxChildren: number;
  staffCount: number;
  childrenCount: number;
  organizationPublicId?: string;
  organizationName?: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
  photoUrls: string[];
  // אינטרנט ורשת
  networkType: number; // 0=לא מוגדר, 1=WiFi, 2=קווי, 3=משולב
  networkName?: string;
  networkPassword?: string;
  internetProvider?: string;
  internetPlanDetails?: string;
  // ציוד
  routerModel?: string;
  tabletModel?: string;
  // שעות פעילות
  openTime?: string;
  closeTime?: string;
  displaySwitchTime?: string;
  latestArrivalTime?: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreateKindergartenRequest {
  name: string;
  code: string;
  city: string;
  street: string;
  houseNumber?: string;
  phone?: string;
  phone2?: string;
  maxChildren?: number;
  organizationPublicId?: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
  photosBase64?: string[];
  openTime?: string;
  closeTime?: string;
  displaySwitchTime?: string;
  latestArrivalTime?: string;
  // אינטרנט ורשת
  networkType?: number;
  networkName?: string;
  networkPassword?: string;
  internetProvider?: string;
  internetPlanDetails?: string;
  // ציוד
  routerModel?: string;
  tabletModel?: string;
}

export interface UpdateKindergartenRequest {
  name?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  phone?: string;
  phone2?: string;
  maxChildren?: number;
  organizationPublicId?: string;
  clearOrganization?: boolean;
  gpsLatitude?: number;
  gpsLongitude?: number;
  photosBase64?: string[];
  removePhotoUrls?: string[];
  // אינטרנט ורשת
  networkType?: number;
  networkName?: string;
  networkPassword?: string;
  internetProvider?: string;
  internetPlanDetails?: string;
  // ציוד
  routerModel?: string;
  tabletModel?: string;
  // שעות פעילות
  openTime?: string;
  closeTime?: string;
  displaySwitchTime?: string;
  latestArrivalTime?: string;
}

export interface AdminChildDto {
  publicId: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  gender?: number;
  kindergartenPublicId: string;
  kindergartenName: string;
  contactsCount: number;
  createdAt: string;
}

export interface SystemParameterDto {
  id: number;
  paramKey: string;
  paramValue: string;
  paramType: string;
  description?: string;
  entityType: number;
  entityId?: number;
  entityName?: string;
  updatedAt?: string;
}

export interface EffectiveParameterDto {
  paramKey: string;
  paramValue: string;
  paramType: string;
  description?: string;
  sourceEntityType: number;
  isOverridden: boolean;
  systemValue: string;
}

// System Calendar types
export interface SystemCalendarDto {
  id: number;
  date: string;          // "yyyy-MM-dd"
  eventType: number;     // 1=חג, 2=חול המועד, 3=חופשה, 4=מלחמה, 5=אחר
  eventName: string;
  operationMode: number; // 0=סגור, 1=רגיל, 2=חלקי
  startTime?: string;    // "HH:mm"
  endTime?: string;      // "HH:mm"
  notes?: string;
  year: number;
}

export interface UpsertSystemCalendarRequest {
  date: string;            // "yyyy-MM-dd"
  eventType: number;
  eventName: string;
  operationMode: number;
  startTime?: string;
  endTime?: string;
  notes?: string;
}

// Kindergarten Calendar types
export interface KindergartenCalendarDto {
  id: number;
  kindergartenId: number;
  kindergartenPublicId: string;
  kindergartenName: string;
  date: string;          // "yyyy-MM-dd"
  eventType: number;     // 1=סגור, 2=אירוע, 3=אחר
  eventName?: string;
  isKindergartenClosed: boolean;
  notes?: string;
}

export interface UpsertKindergartenCalendarRequest {
  kindergartenPublicIds: string[];
  date: string;            // "yyyy-MM-dd"
  eventType: number;       // 1=סגור, 2=אירוע, 3=אחר
  eventName?: string;
  isKindergartenClosed: boolean;
  notes?: string;
}

export const adminApi = {
  // Dashboard
  getDashboard: async (organizationId?: string): Promise<ApiResponse<AdminDashboardStats>> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<ApiResponse<AdminDashboardStats>>('/management/dashboard', { params });
    return response.data;
  },

  // Organizations
  getOrganizations: async (): Promise<ApiResponse<OrganizationDto[]>> => {
    const response = await api.get<ApiResponse<OrganizationDto[]>>('/management/organizations');
    return response.data;
  },

  createOrganization: async (data: CreateOrganizationRequest): Promise<ApiResponse<OrganizationDto>> => {
    const response = await api.post<ApiResponse<OrganizationDto>>('/management/organizations', data);
    return response.data;
  },

  updateOrganization: async (publicId: string, data: UpdateOrganizationRequest): Promise<ApiResponse<OrganizationDto>> => {
    const response = await api.put<ApiResponse<OrganizationDto>>(`/management/organizations/${publicId}`, data);
    return response.data;
  },

  createOrganizationManager: async (orgPublicId: string, data: CreateOrganizationManagerRequest): Promise<ApiResponse<AdminStaffDto>> => {
    const response = await api.post<ApiResponse<AdminStaffDto>>(`/management/organizations/${orgPublicId}/manager`, data);
    return response.data;
  },

  // Staff
  getStaff: async (organizationId?: string): Promise<ApiResponse<AdminStaffDto[]>> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<ApiResponse<AdminStaffDto[]>>('/management/staff', { params });
    return response.data;
  },

  createStaff: async (data: CreateStaffRequest): Promise<ApiResponse<AdminStaffDto>> => {
    const response = await api.post<ApiResponse<AdminStaffDto>>('/management/staff', data);
    return response.data;
  },

  updateStaff: async (publicId: string, data: UpdateStaffRequest): Promise<ApiResponse<AdminStaffDto>> => {
    const response = await api.put<ApiResponse<AdminStaffDto>>(`/management/staff/${publicId}`, data);
    return response.data;
  },

  deleteStaff: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/staff/${publicId}`);
    return response.data;
  },

  // Kindergartens CRUD
  getKindergartens: async (organizationId?: string): Promise<ApiResponse<AdminKindergartenDto[]>> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<ApiResponse<AdminKindergartenDto[]>>('/management/kindergartens', { params });
    return response.data;
  },

  createKindergarten: async (data: CreateKindergartenRequest): Promise<ApiResponse<AdminKindergartenDto>> => {
    const response = await api.post<ApiResponse<AdminKindergartenDto>>('/management/kindergartens', data);
    return response.data;
  },

  updateKindergarten: async (publicId: string, data: UpdateKindergartenRequest): Promise<ApiResponse<AdminKindergartenDto>> => {
    const response = await api.put<ApiResponse<AdminKindergartenDto>>(`/management/kindergartens/${publicId}`, data);
    return response.data;
  },

  deleteKindergarten: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/kindergartens/${publicId}`);
    return response.data;
  },

  // Children
  getChildren: async (organizationId?: string): Promise<ApiResponse<AdminChildDto[]>> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<ApiResponse<AdminChildDto[]>>('/management/children', { params });
    return response.data;
  },

  transferChild: async (childPublicId: string, targetKindergartenPublicId: string): Promise<ApiResponse<AdminChildDto>> => {
    const response = await api.put<ApiResponse<AdminChildDto>>(`/management/children/${childPublicId}/transfer`, {
      targetKindergartenPublicId,
    });
    return response.data;
  },

  // Kindergarten search
  searchKindergartens: async (query: string, organizationId?: string): Promise<ApiResponse<AdminKindergartenSearchDto[]>> => {
    const params: Record<string, string> = { q: query };
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<AdminKindergartenSearchDto[]>>('/management/kindergartens/search', {
      params,
    });
    return response.data;
  },

  // Lookups
  getRoleTypes: async (): Promise<ApiResponse<RoleTypeDto[]>> => {
    const response = await api.get<ApiResponse<RoleTypeDto[]>>('/management/role-types');
    return response.data;
  },

  // System Parameters
  getParameters: async (entityType?: number, entityId?: number): Promise<ApiResponse<SystemParameterDto[]>> => {
    const params: Record<string, any> = {};
    if (entityType !== undefined) params.entityType = entityType;
    if (entityId !== undefined) params.entityId = entityId;
    const response = await api.get<ApiResponse<SystemParameterDto[]>>('/management/parameters', { params });
    return response.data;
  },

  updateParameters: async (
    entityType: number,
    entityId: number | null,
    parameters: { paramKey: string; paramValue: string }[],
    orgPublicId?: string
  ): Promise<ApiResponse<SystemParameterDto[]>> => {
    const params: Record<string, any> = { entityType };
    if (entityId !== null) params.entityId = entityId;
    if (orgPublicId) params.orgPublicId = orgPublicId;
    const response = await api.put<ApiResponse<SystemParameterDto[]>>(
      '/management/parameters',
      { parameters },
      { params }
    );
    return response.data;
  },

  getEffectiveParameters: async (entityType: number, orgPublicId?: string): Promise<ApiResponse<EffectiveParameterDto[]>> => {
    const params: Record<string, any> = { entityType };
    if (orgPublicId) params.orgPublicId = orgPublicId;
    const response = await api.get<ApiResponse<EffectiveParameterDto[]>>('/management/parameters/effective', { params });
    return response.data;
  },

  deleteParameterOverride: async (entityType: number, orgPublicId: string, paramKey: string): Promise<ApiResponse<boolean>> => {
    const params = { entityType, orgPublicId, paramKey };
    const response = await api.delete<ApiResponse<boolean>>('/management/parameters/override', { params });
    return response.data;
  },

  checkUsername: async (username: string): Promise<ApiResponse<{ available: boolean }>> => {
    const response = await api.get<ApiResponse<{ available: boolean }>>('/management/staff/check-username', { params: { username } });
    return response.data;
  },

  // Admin login
  adminLogin: async (username: string, password: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/admin-login', {
      username,
      password,
    });
    return response.data;
  },

  // ==========================================
  // Product Catalog — Network Manager
  // ==========================================

  getProducts: async (organizationId?: string): Promise<ApiResponse<any[]>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<any[]>>('/management/products', { params });
    return response.data;
  },

  createProduct: async (data: { name: string; categoryId?: number; defaultUnit?: string; estimatedPrice?: number; isChargeable: boolean }, organizationId?: string): Promise<ApiResponse<any>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.post<ApiResponse<any>>('/management/products', data, { params });
    return response.data;
  },

  updateProduct: async (publicId: string, data: { name?: string; categoryId?: number; defaultUnit?: string; estimatedPrice?: number; isChargeable?: boolean; isActive?: boolean }): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/management/products/${publicId}`, data);
    return response.data;
  },

  deleteProduct: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/products/${publicId}`);
    return response.data;
  },

  getProductRequests: async (organizationId?: string): Promise<ApiResponse<any[]>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<any[]>>('/management/product-requests', { params });
    return response.data;
  },

  reviewProductRequest: async (requestId: number, action: 'approve' | 'reject', notes?: string): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/management/product-requests/${requestId}`, { action, notes });
    return response.data;
  },

  // ==========================================
  // Budget Dashboard — Network Manager
  // ==========================================

  getBudgetDashboard: async (organizationId?: string): Promise<ApiResponse<any>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<any>>('/management/budget-dashboard', { params });
    return response.data;
  },

  getBudgetExpenses: async (organizationId?: string, dateFrom?: string, dateTo?: string): Promise<ApiResponse<any[]>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await api.get<ApiResponse<any[]>>('/management/budget-expenses', { params });
    return response.data;
  },

  // ==========================================
  // System Products — SysAdmin
  // ==========================================

  getSystemProducts: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/sysadmin/products');
    return response.data;
  },

  createSystemProduct: async (data: { name: string; categoryId?: number; defaultUnit?: string; estimatedPrice?: number; isChargeable: boolean }): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/sysadmin/products', data);
    return response.data;
  },

  updateSystemProduct: async (publicId: string, data: { name?: string; categoryId?: number; defaultUnit?: string; estimatedPrice?: number; isChargeable?: boolean; isActive?: boolean }): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/sysadmin/products/${publicId}`, data);
    return response.data;
  },

  deleteSystemProduct: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/sysadmin/products/${publicId}`);
    return response.data;
  },

  // Categories — merged (system + org) for dropdowns
  getCategories: async (organizationId?: string): Promise<ApiResponse<any[]>> => {
    if (organizationId) {
      const response = await api.get<ApiResponse<any[]>>('/management/all-categories', { params: { organizationId } });
      return response.data;
    } else {
      const response = await api.get<ApiResponse<any[]>>('/sysadmin/categories');
      return response.data;
    }
  },

  // Category CRUD — Network Manager (Organization Level)
  getOrgCategories: async (organizationId?: string): Promise<ApiResponse<any[]>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<any[]>>('/management/categories', { params });
    return response.data;
  },

  createCategory: async (data: { name: string; icon?: string; sortOrder: number }, organizationId?: string): Promise<ApiResponse<any>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.post<ApiResponse<any>>('/management/categories', data, { params });
    return response.data;
  },

  updateCategory: async (id: number, data: { name?: string; icon?: string; sortOrder?: number }): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/management/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/categories/${id}`);
    return response.data;
  },

  // Category CRUD — SysAdmin (System Level)
  getSystemCategories: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/sysadmin/categories');
    return response.data;
  },

  createSystemCategory: async (data: { name: string; icon?: string; sortOrder: number }): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/sysadmin/categories', data);
    return response.data;
  },

  updateSystemCategory: async (id: number, data: { name?: string; icon?: string; sortOrder?: number }): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/sysadmin/categories/${id}`, data);
    return response.data;
  },

  deleteSystemCategory: async (id: number): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/sysadmin/categories/${id}`);
    return response.data;
  },

  // Attendance History
  getAttendanceHistory: async (params: {
    kindergartenId: number;
    dateFrom: string;
    dateTo: string;
    childId?: number;
  }): Promise<ApiResponse<AttendanceHistoryRecord[]>> => {
    const queryParams: Record<string, string> = {
      kindergartenId: params.kindergartenId.toString(),
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    };
    if (params.childId) queryParams.childId = params.childId.toString();
    const response = await api.get<ApiResponse<AttendanceHistoryRecord[]>>(
      '/management/attendance-history', { params: queryParams });
    return response.data;
  },

  getAttendanceKindergartens: async (organizationId?: string): Promise<ApiResponse<AttendanceKindergartenItem[]>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<AttendanceKindergartenItem[]>>(
      '/management/attendance-kindergartens', { params });
    return response.data;
  },

  // ==========================================
  // System Calendar — לוח שנה ארצי
  // ==========================================

  getSystemCalendar: async (year: number): Promise<ApiResponse<SystemCalendarDto[]>> => {
    const response = await api.get<ApiResponse<SystemCalendarDto[]>>('/management/system-calendar', { params: { year } });
    return response.data;
  },

  upsertSystemCalendarEntry: async (data: UpsertSystemCalendarRequest): Promise<ApiResponse<SystemCalendarDto>> => {
    const response = await api.post<ApiResponse<SystemCalendarDto>>('/management/system-calendar', data);
    return response.data;
  },

  bulkUpsertSystemCalendar: async (entries: UpsertSystemCalendarRequest[]): Promise<ApiResponse<SystemCalendarDto[]>> => {
    const response = await api.post<ApiResponse<SystemCalendarDto[]>>('/management/system-calendar/bulk', { entries });
    return response.data;
  },

  deleteSystemCalendarEntry: async (id: number): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/system-calendar/${id}`);
    return response.data;
  },

  // ==========================================
  // Kindergarten Calendar — לוח שנה גנים (דריסות)
  // ==========================================

  getKindergartenCalendar: async (year: number, organizationId?: string): Promise<ApiResponse<KindergartenCalendarDto[]>> => {
    const params: Record<string, unknown> = { year };
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get<ApiResponse<KindergartenCalendarDto[]>>('/management/kindergarten-calendar', { params });
    return response.data;
  },

  upsertKindergartenCalendar: async (data: UpsertKindergartenCalendarRequest): Promise<ApiResponse<KindergartenCalendarDto[]>> => {
    const response = await api.post<ApiResponse<KindergartenCalendarDto[]>>('/management/kindergarten-calendar', data);
    return response.data;
  },

  deleteKindergartenCalendarEntry: async (id: number): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/management/kindergarten-calendar/${id}`);
    return response.data;
  },
};

// ==========================================
// Attendance History Types
// ==========================================

export interface AttendanceHistoryRecord {
  childId: number;
  childPublicId: string;
  childName: string;
  kindergartenName: string;
  date: string;
  arrivedAt: string;
  expectedArrivalTime: string;
  source: string;
  kindergartenId: number;
  markedByUserId: number;
  markedByName: string;
  latitude?: number;
  longitude?: number;
}

export interface AttendanceKindergartenItem {
  id: number;
  publicId: string;
  name: string;
}

// ==========================================
// Call Center Types & API — מוקד חרום
// ==========================================

export interface ParentContactInfo {
  name: string;
  phone: string;
}

export interface ActionLogEntry {
  timestamp: string;
  userId: number;
  userName: string;
  action: string;
  details: string;
}

export interface CallCenterAlertDto {
  alertId: string;
  date: string;
  childId: number;
  childName: string;
  kindergartenId: number;
  organizationId: number;
  kindergartenName: string;
  kindergartenPhone: string;
  parentContacts: ParentContactInfo[];
  alertTime: string;
  expectedArrivalTime: string;
  alertType: string;
  alertLevel: number;
  status: string;
  isResolved: boolean;
  assignedTo?: string;
  pickedUpAt?: string;
  resolvedAt?: string;
  resolution?: string;
  actionLog: ActionLogEntry[];
}

export interface CallCenterStats {
  watching: number;
  level1Watching: number;
  level2Watching: number;
  openAlerts: number;
  inProgressAlerts: number;
  resolvedToday: number;
  falseAlarmToday: number;
  totalToday: number;
}

export interface UpdateCallCenterAlertRequest {
  status: string;
  resolution?: string;
}

export interface AddActionLogRequest {
  action: string;
  details: string;
}

export const callCenterApi = {
  getAlerts: async (filterParams?: { status?: string; level?: number; organizationId?: number; from?: string; to?: string }): Promise<ApiResponse<CallCenterAlertDto[]>> => {
    const params: Record<string, string> = {};
    if (filterParams?.status) params.status = filterParams.status;
    if (filterParams?.level) params.level = filterParams.level.toString();
    if (filterParams?.organizationId) params.organizationId = filterParams.organizationId.toString();
    if (filterParams?.from) params.from = filterParams.from;
    if (filterParams?.to) params.to = filterParams.to;
    const response = await api.get<ApiResponse<CallCenterAlertDto[]>>('/callcenter/alerts', { params });
    return response.data;
  },

  getAlert: async (date: string, alertId: string): Promise<ApiResponse<CallCenterAlertDto>> => {
    const response = await api.get<ApiResponse<CallCenterAlertDto>>(`/callcenter/alerts/${date}/${alertId}`);
    return response.data;
  },

  updateAlertStatus: async (date: string, alertId: string, data: UpdateCallCenterAlertRequest): Promise<ApiResponse<CallCenterAlertDto>> => {
    const response = await api.put<ApiResponse<CallCenterAlertDto>>(`/callcenter/alerts/${date}/${alertId}/status`, data);
    return response.data;
  },

  addActionLog: async (date: string, alertId: string, data: AddActionLogRequest): Promise<ApiResponse<CallCenterAlertDto>> => {
    const response = await api.post<ApiResponse<CallCenterAlertDto>>(`/callcenter/alerts/${date}/${alertId}/actions`, data);
    return response.data;
  },

  getStats: async (organizationId?: number): Promise<ApiResponse<CallCenterStats>> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId.toString();
    const response = await api.get<ApiResponse<CallCenterStats>>('/callcenter/stats', { params });
    return response.data;
  },
};

export const authApi = {
  // Send OTP
  sendOtp: async (phone: string): Promise<ApiResponse<{ otpSent: boolean }>> => {
    const response = await api.post<ApiResponse<{ otpSent: boolean }>>('/auth/send-otp', { Phone: phone });
    return response.data;
  },

  // Verify OTP
  verifyOtp: async (phone: string, otp: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post<ApiResponse<{ token: string; user: any }>>('/auth/verify-otp', { Phone: phone, Otp: otp });
    return response.data;
  },
  
  // Staff login (tablet)
  staffLogin: async (loginIdentifier: string, password: string, kindergartenCode: string, deviceFingerprint?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/staff-login', {
      LoginIdentifier: loginIdentifier,
      Password: password,
      KindergartenCode: kindergartenCode,
      DeviceFingerprint: deviceFingerprint,
    });
    return response.data;
  },

  // Parent password login
  parentLogin: async (phone: string, password: string, deviceFingerprint?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/parent-login', {
      Phone: phone,
      Password: password,
      DeviceFingerprint: deviceFingerprint,
    });
    return response.data;
  },

  // Change password (requires auth token)
  changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/change-password', {
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
    });
    return response.data;
  },

  // Reset password (forgot password - requires OTP)
  resetPassword: async (phone: string, otp: string, newPassword: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/reset-password', {
      Phone: phone,
      Otp: otp,
      NewPassword: newPassword,
    });
    return response.data;
  },

  // Teacher login (no kindergartenCode)
  teacherLogin: async (loginIdentifier: string, password: string, deviceFingerprint?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/teacher-login', {
      LoginIdentifier: loginIdentifier,
      Password: password,
      DeviceFingerprint: deviceFingerprint,
    });
    return response.data;
  },

  // Teacher get kindergartens (for use after unified login)
  getTeacherKindergartens: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/teacher/kindergartens');
    return response.data;
  },

  // Teacher select kindergarten
  teacherSelectKg: async (kindergartenPublicId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auth/teacher-select-kg', {
      KindergartenPublicId: kindergartenPublicId,
    });
    return response.data;
  },

  // חידוש access token באמצעות refresh token
  refreshAccessToken: async (refreshToken: string, deviceFingerprint?: string): Promise<ApiResponse<any>> => {
    const functionKey = import.meta.env.VITE_FUNCTION_KEY;
    const baseUrl = import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api';
    // קריאה ישירה בלי interceptor כדי למנוע לולאה אינסופית
    const response = await axios.post(
      `${baseUrl}/auth/refresh`,
      { refreshToken, deviceFingerprint },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(functionKey && { 'x-functions-key': functionKey }),
        },
        timeout: 15000,
      }
    );
    return response.data;
  },
};

// ==========================================
// Teacher API
// ==========================================

// Compress image to max dimension and JPEG quality (for invoice OCR upload)
async function compressImage(file: File, maxDim: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    // For PDFs, just convert to base64 as-is (can't resize)
    if (file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Scale down if larger than maxDim
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image'));

    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result as string; };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const teacherApi = {
  // Budget Sources
  getBudgetSources: async (year?: number): Promise<ApiResponse<any[]>> => {
    const params: any = {};
    if (year) params.year = year;
    const response = await api.get<ApiResponse<any[]>>('/teacher/budgets', { params });
    return response.data;
  },

  createBudgetSource: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/teacher/budgets', data);
    return response.data;
  },

  updateBudgetSource: async (publicId: string, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/budgets/${publicId}`, data);
    return response.data;
  },

  deleteBudgetSource: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/teacher/budgets/${publicId}`);
    return response.data;
  },

  getBudgetSummary: async (): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/teacher/budget-summary');
    return response.data;
  },

  // Expenses
  getExpenses: async (params?: {
    budgetSourcePublicId?: string;
    dateFrom?: string;
    dateTo?: string;
    categoryId?: number;
  }): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/teacher/expenses', { params });
    return response.data;
  },

  createExpense: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/teacher/expenses', data);
    return response.data;
  },

  updateExpense: async (publicId: string, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/expenses/${publicId}`, data);
    return response.data;
  },

  deleteExpense: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/teacher/expenses/${publicId}`);
    return response.data;
  },

  validateExpense: async (budgetSourceId: string, amount: number, categoryId?: number): Promise<ApiResponse<any>> => {
    const params: any = { budgetSourceId, amount };
    if (categoryId) params.categoryId = categoryId;
    const response = await api.get<ApiResponse<any>>('/teacher/expenses/validate', { params });
    return response.data;
  },

  // Categories
  getCategories: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/teacher/categories');
    return response.data;
  },

  // Products (catalog)
  getProducts: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get<ApiResponse<any[]>>('/teacher/products');
    return response.data;
  },

  requestProductAddition: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/teacher/products/request-addition', data);
    return response.data;
  },

  // Purchase Lists
  getPurchaseLists: async (status?: number): Promise<ApiResponse<any[]>> => {
    const params: any = {};
    if (status !== undefined) params.status = status;
    const response = await api.get<ApiResponse<any[]>>('/teacher/purchases', { params });
    return response.data;
  },

  getPurchaseList: async (publicId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/teacher/purchases/${publicId}`);
    return response.data;
  },

  createPurchaseList: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/teacher/purchases', data);
    return response.data;
  },

  updatePurchaseList: async (publicId: string, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/purchases/${publicId}`, data);
    return response.data;
  },

  deletePurchaseList: async (publicId: string): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/teacher/purchases/${publicId}`);
    return response.data;
  },

  // Purchase List Items
  addPurchaseItem: async (listPublicId: string, data: any): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/teacher/purchases/${listPublicId}/items`, data);
    return response.data;
  },

  updatePurchaseItem: async (_listPublicId: string, itemId: number, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/purchase-items/${itemId}`, data);
    return response.data;
  },

  removePurchaseItem: async (_listPublicId: string, itemId: number): Promise<ApiResponse<string>> => {
    const response = await api.delete<ApiResponse<string>>(`/teacher/purchase-items/${itemId}`);
    return response.data;
  },

  togglePurchaseItem: async (_listPublicId: string, itemId: number): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/purchase-items/${itemId}/toggle`, {});
    return response.data;
  },

  convertToExpense: async (listPublicId: string, budgetSourcePublicId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/teacher/purchases/${listPublicId}/convert`, {
      budgetSourcePublicId: budgetSourcePublicId,
    });
    return response.data;
  },

  // Invoice OCR
  uploadInvoice: async (file: File): Promise<ApiResponse<any>> => {
    // Resize image on client side to avoid OutOfMemoryException on server
    const compressedBase64 = await compressImage(file, 1600, 0.8);
    const response = await api.post<ApiResponse<any>>('/teacher/invoices/upload', {
      image: compressedBase64,
      fileName: file.name,
      contentType: 'image/jpeg',
    });
    return response.data;
  },

  getInvoiceOcr: async (expensePublicId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/teacher/invoices/${expensePublicId}/ocr`);
    return response.data;
  },

  verifyInvoice: async (expensePublicId: string, corrections: any): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/teacher/invoices/${expensePublicId}/verify`, corrections);
    return response.data;
  },
};

// ==========================================
// שערי נוכחות ותגי BLE
// ==========================================

export type GateDeviceType = 'Gate' | 'Shelter' | 'Tablet';
export type GateDeviceMode = 'Shadow' | 'Live';

export interface KindergartenDeviceDto {
  publicId: string;
  kindergartenPublicId: string;
  kindergartenName: string;
  name: string;
  deviceType: GateDeviceType;
  gatewayMac?: string | null;
  rssiThreshold: number;
  minReads: number;
  windowSeconds: number;
  mode: GateDeviceMode;
  isActive: boolean;
  lastHeartbeatAt?: string | null;
  serialNumber?: string | null;
  model?: string | null;
  firmwareVersion?: string | null;
  purchasedAt?: string | null;
  installedAt?: string | null;
  warrantyUntil?: string | null;
  placementRole?: 'entrance' | 'bags' | null;
  connectionType?: 'wifi' | 'ethernet' | 'ble' | null;
  notes?: string | null;
}

export interface CreateGateDeviceRequest {
  kindergartenPublicId: string;
  name: string;
  deviceType: GateDeviceType;
  gatewayMac?: string;
  rssiThreshold?: number;
  minReads?: number;
  windowSeconds?: number;
}

export interface UpdateGateDeviceRequest {
  name?: string;
  gatewayMac?: string;
  rssiThreshold?: number;
  minReads?: number;
  windowSeconds?: number;
  mode?: GateDeviceMode;
  isActive?: boolean;
  // פרטי ציוד: מחרוזת ריקה מנקה שדה
  serialNumber?: string;
  model?: string;
  firmwareVersion?: string;
  purchasedAt?: string;
  installedAt?: string;
  warrantyUntil?: string;
  placementRole?: string;
  connectionType?: string;
  notes?: string;
}

// תגובת יצירה / החלפת מפתח - deviceKey מוצג פעם אחת בלבד
export interface GateDeviceKeyResult {
  device: KindergartenDeviceDto;
  deviceKey: string;
  gatewayUrlPath: string;
}

export interface ChildTagDto {
  publicId: string;
  childPublicId: string;
  tagIdentifier: string;
  isActive: boolean;
  assignedAt: string;
  lastSeenAt?: string | null;
  batteryPercent?: number | null;
  notes?: string | null;
}

export interface AssignChildTagRequest {
  tagIdentifier: string;
  notes?: string;
}

export const gateApi = {
  getDevices: async (kindergartenId?: string): Promise<ApiResponse<KindergartenDeviceDto[]>> => {
    const params = kindergartenId ? { kindergartenId } : {};
    const response = await api.get<ApiResponse<KindergartenDeviceDto[]>>('/management/gate-devices', { params });
    return response.data;
  },

  createDevice: async (data: CreateGateDeviceRequest): Promise<ApiResponse<GateDeviceKeyResult>> => {
    const response = await api.post<ApiResponse<GateDeviceKeyResult>>('/management/gate-devices', data);
    return response.data;
  },

  updateDevice: async (id: string, data: UpdateGateDeviceRequest): Promise<ApiResponse<KindergartenDeviceDto>> => {
    const response = await api.put<ApiResponse<KindergartenDeviceDto>>(`/management/gate-devices/${id}`, data);
    return response.data;
  },

  rotateKey: async (id: string): Promise<ApiResponse<GateDeviceKeyResult>> => {
    const response = await api.post<ApiResponse<GateDeviceKeyResult>>(`/management/gate-devices/${id}/rotate-key`);
    return response.data;
  },

  deleteDevice: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/management/gate-devices/${id}`);
    return response.data;
  },

  getChildTags: async (childPublicId: string): Promise<ApiResponse<ChildTagDto[]>> => {
    const response = await api.get<ApiResponse<ChildTagDto[]>>(`/management/children/${childPublicId}/tags`);
    return response.data;
  },

  assignChildTag: async (childPublicId: string, data: AssignChildTagRequest): Promise<ApiResponse<ChildTagDto>> => {
    const response = await api.put<ApiResponse<ChildTagDto>>(`/management/children/${childPublicId}/tag`, data);
    return response.data;
  },

  removeChildTag: async (childPublicId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/management/children/${childPublicId}/tag`);
    return response.data;
  },
};

export default api;
